import React, { Dispatch, SetStateAction } from "react"
import Popup from "../../Ui/Popup"
import GET_ADDRESS_MODIFICATION from "../../../graphql/queries/order/GetAddressModification"
import { useMutation, useQuery } from "@apollo/client"
import DELETE_ADDRESSES_MODIFICATION from "../../../graphql/mutations/order/modification/address/DeleteModificationAddresses"
import { handleErrorMessages } from "helpers/errors"
import { Loader } from "../../Ui/Loader"
import { AddressType } from "lib/types/order"
import START_ADDRESS_MODIFICATION from "../../../graphql/mutations/order/modification/address/StartOrderModificationAddresses"
import alertActions from "lib/store/services/Alert/AlertSlice"
import { useDispatch } from "react-redux"
import { OrderModificationAddresses } from "lib/types/generated/graphql-types"
import ModificationAddressReason from "./ModificationAddressReason"
import { Tab, TabList, TabPanel, Tabs } from "react-tabs"
import { theme } from "../../../theme"
import AddressInfo from "./AddressInfo"
import { useUserGroups } from "../../../hooks/useUserGroups"
import { PermissionGroups } from "../../../routes/constants"

type Props = {
  addressType: AddressType
  setShowPopup: Dispatch<SetStateAction<boolean>>
  modificationId: string
  refetch: () => void
  refetchLoading: boolean
}

const ConfirmPopup = ({
  setShowPopup,
  modificationId,
  refetch,
  refetchLoading,
  addressType
}: Props) => {
  const { data, loading: fetchLoading } = useQuery(GET_ADDRESS_MODIFICATION, {
    variables: { modificationId }
  })
  const dispatch = useDispatch()
  const modification: OrderModificationAddresses = data?.Order?.orderModificationAddresses
  const addresses = data?.Order?.orderModificationAddresses?.addresses.data
  const address =
    addressType === AddressType.SHIPPING_ADDRESS
      ? addresses?.shippingAddress
      : addresses?.billingAddress

  const [deleteModificationAddress, { loading: mutationLoading }] = useMutation(
    DELETE_ADDRESSES_MODIFICATION,
    {
      variables: { modificationId },
      onCompleted: () => {
        refetch()
        setShowPopup(false)
      },
      onError: (e) => handleErrorMessages(e)
    }
  )

  const [startModificationAddress, { loading: startLoading }] = useMutation(
    START_ADDRESS_MODIFICATION,
    {
      variables: { modificationId, input: JSON.stringify({}) },
      onCompleted: () => {
        dispatch(
          alertActions.actions.createAlert({
            message: `${addressType === AddressType.SHIPPING_ADDRESS ? "Shipping" : "Billing"} address successfully updated`,
            type: "success"
          })
        )
        refetch()
        setShowPopup(false)
      },
      onError: (e) => handleErrorMessages(e)
    }
  )

  const {hasAnyGroup} = useUserGroups()
  const hasAccess = hasAnyGroup(PermissionGroups.orders.execute)

  if (fetchLoading || refetchLoading || !address)
    return (
      <Popup
        title={`Update ${addressType === AddressType.SHIPPING_ADDRESS ? "shipping" : "billing"} address`}
        handleCloseClick={() => setShowPopup(false)}
        buttonText={"Confirm"}
        disableOkButton={true}
      >
        <Loader color={"black"} />
      </Popup>
    )

  return (
    <Popup
      title={`Update ${addressType === AddressType.SHIPPING_ADDRESS ? "shipping" : "billing"} address`}
      handleCloseClick={() => setShowPopup(false)}
      buttonText={hasAccess ? "Confirm" : "Needs admin access"}
      disableOkButton={!hasAccess}
      handleOkClick={() => hasAccess ? startModificationAddress() : {}}
      loading={startLoading}
      cancelOperation={deleteModificationAddress}
      cancelOperationLoading={mutationLoading}
      backgroundColor={theme.colors.white}
    >
      <Tabs>
        <TabList>
          <Tab>Reason</Tab>
          <Tab>Updated address</Tab>
        </TabList>
        <TabPanel>
          <ModificationAddressReason
            modification={modification}
            refetch={refetch}
            orderLoading={refetchLoading}
          />
        </TabPanel>
        <TabPanel>
          <AddressInfo address={address} showCustomerInfo={true} />
        </TabPanel>
      </Tabs>
    </Popup>
  )
}

export default ConfirmPopup
